/**
 * Chat with AI — desktop sidebar destination.
 *
 * Uses the collected-turn HTTP API (same agent as iOS). Write proposals are
 * confirmed here, then applied through the existing allocation/budget APIs.
 */

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  createChatSession,
  deleteChatSession,
  getChatMessages,
  listChatSessions,
  postChatTurn,
  type ChatArtifact,
  type ChatMessage,
  type ChatProposal,
  type ChatSession,
} from '../api/client';
import { applyProposal, proposalConfirmLabel } from '../chat/applyProposal';
import { PageHeader } from '../components/AppShell';
import { Button, EmptyState } from '../components/ui';
import { useToast } from '../components/ui/ToastProvider';
import { ChatIcon } from '../components/icons';
import type { Budget } from '../budget/useBudget';
import './pages.css';

const STARTERS = [
  'Will Groceries last until payday?',
  'What is this Chick-fil-A charge?',
  'Spending on eating out this month',
];

interface ChatPageProps {
  budget: Budget;
}

export function ChatPage({ budget }: ChatPageProps) {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toolStatus, setToolStatus] = useState<string | null>(null);
  const [prefillTransactionId, setPrefillTransactionId] = useState<string | undefined>();
  const [pendingAutoSend, setPendingAutoSend] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const consumedPrefill = useRef(false);

  useEffect(() => {
    void (async () => {
      try {
        const { sessions: list } = await listChatSessions();
        setSessions(list);
        if (list[0]) {
          setSessionId(list[0].id);
          const { messages: msgs } = await getChatMessages(list[0].id);
          setMessages(msgs);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Couldn't load chats.");
      }
    })();
  }, []);

  useEffect(() => {
    if (consumedPrefill.current) return;
    const q = searchParams.get('q');
    const txId = searchParams.get('prefillTransactionId');
    const auto = searchParams.get('autosend') === '1';
    if (!q && !txId) return;
    consumedPrefill.current = true;
    if (txId) setPrefillTransactionId(txId);
    if (q && auto) setPendingAutoSend(q);
    else if (q) setDraft(q);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!pendingAutoSend) return;
    const text = pendingAutoSend;
    setPendingAutoSend(null);
    void send(text);
  }, [pendingAutoSend]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, toolStatus, busy]);

  async function ensureSession(): Promise<string> {
    if (sessionId) return sessionId;
    const session = await createChatSession('New chat');
    setSessions((current) => [session, ...current]);
    setSessionId(session.id);
    setMessages([]);
    return session.id;
  }

  async function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    setToolStatus(null);
    setDraft('');
    const txPrefill = prefillTransactionId;
    setPrefillTransactionId(undefined);
    const optimistic: ChatMessage = {
      id: `local-${Date.now()}`,
      role: 'user',
      content: trimmed,
      createdAt: new Date().toISOString(),
    };
    setMessages((current) => [...current, optimistic]);
    try {
      const id = await ensureSession();
      const result = await postChatTurn(id, trimmed, txPrefill);
      for (const event of result.events) {
        if (event.type === 'tool.start') setToolStatus(`Working: ${event.name ?? 'tool'}…`);
        if (event.type === 'tool.done') setToolStatus(null);
        if (event.type === 'error') setError(event.message ?? "Couldn't finish that reply.");
      }
      setMessages(result.messages);
      setSessions((current) =>
        current.map((session) =>
          session.id === id
            ? { ...session, title: session.title === 'New chat' ? trimmed.slice(0, 48) : session.title }
            : session
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't reach the assistant.");
    } finally {
      setBusy(false);
      setToolStatus(null);
    }
  }

  async function confirmProposal(proposal: ChatProposal) {
    try {
      await applyProposal(proposal, budget);
      setMessages((current) =>
        current.map((message) =>
          message.proposal?.id === proposal.id
            ? {
                ...message,
                proposal: null,
                content: message.content
                  ? `${message.content}\n\nDone.`
                  : `Done: ${proposal.summary}`,
              }
            : message
        )
      );
      toast.show(`Done: ${proposal.summary}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't apply that change.");
    }
  }

  return (
    <>
      <PageHeader
        title="Chat with AI"
        subtitle="Ask about the budget. Changes wait for your confirm."
        action={
          <Button
            size="sm"
            emphasis="secondary"
            onClick={() =>
              void (async () => {
                const session = await createChatSession('New chat');
                setSessions((current) => [session, ...current]);
                setSessionId(session.id);
                setMessages([]);
                setError(null);
              })()
            }
          >
            New chat
          </Button>
        }
      />

      <div className="chat-layout">
        <aside className="chat-sessions card" aria-label="Past chats">
          {sessions.length === 0 ? (
            <p className="section__meta">No chats yet.</p>
          ) : (
            <ul className="chat-session-list">
              {sessions.map((session) => (
                <li key={session.id}>
                  <button
                    type="button"
                    className={session.id === sessionId ? 'chat-session is-active' : 'chat-session'}
                    onClick={() =>
                      void (async () => {
                        setSessionId(session.id);
                        const { messages: msgs } = await getChatMessages(session.id);
                        setMessages(msgs);
                      })()
                    }
                  >
                    {session.title}
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={`Delete ${session.title}`}
                    onClick={() =>
                      void (async () => {
                        await deleteChatSession(session.id);
                        setSessions((current) => current.filter((row) => row.id !== session.id));
                        if (sessionId === session.id) {
                          setSessionId(null);
                          setMessages([]);
                        }
                      })()
                    }
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="chat-thread card">
          {messages.length === 0 && !busy ? (
            <EmptyState
              title="Ask about the budget"
              body="Look up charges, check envelopes, or draft a report."
              action={
                <div className="stack" style={{ gap: 'var(--space-xs)' }}>
                  {STARTERS.map((prompt) => (
                    <Button key={prompt} size="sm" onClick={() => void send(prompt)}>
                      {prompt}
                    </Button>
                  ))}
                </div>
              }
            />
          ) : (
            <div className="chat-messages">
              {messages.map((message) => (
                <MessageBlock
                  key={message.id}
                  message={message}
                  busy={busy}
                  onConfirm={(proposal) => void confirmProposal(proposal)}
                  onDismiss={(proposal) =>
                    setMessages((current) =>
                      current.map((row) =>
                        row.proposal?.id === proposal.id
                          ? { ...row, proposal: null, content: row.content || 'Okay — left it alone.' }
                          : row
                      )
                    )
                  }
                />
              ))}
              {(busy || toolStatus) && (
                <p className="chat-tool">{toolStatus || 'Investigating…'}</p>
              )}
              <div ref={bottomRef} />
            </div>
          )}

          {error && (
            <p className="chat-error" role="alert">
              {error}
            </p>
          )}

          <form
            className="chat-composer"
            onSubmit={(event) => {
              event.preventDefault();
              void send(draft);
            }}
          >
            <input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Ask about the budget"
              aria-label="Message"
              disabled={busy}
            />
            <Button type="submit" loading={busy} disabled={!draft.trim()}>
              Send
            </Button>
          </form>
        </section>
      </div>
    </>
  );
}

function MessageBlock({
  message,
  busy,
  onConfirm,
  onDismiss,
}: {
  message: ChatMessage;
  busy: boolean;
  onConfirm: (proposal: ChatProposal) => void;
  onDismiss: (proposal: ChatProposal) => void;
}) {
  const isUser = message.role === 'user';
  return (
    <div className={isUser ? 'chat-bubble chat-bubble--user' : 'chat-bubble'}>
      {!isUser && <ChatIcon width={16} height={16} />}
      <div>
        {message.content && <p>{message.content}</p>}
        {message.proposal && (
          <div className="chat-proposal">
            {message.proposal.kind === 'categorize' && (
              <p className="section__meta">Categorize this charge?</p>
            )}
            <p>{message.proposal.summary}</p>
            {message.proposal.destructive && (
              <p className="chat-warning">This moves the charge to trash.</p>
            )}
            <div className="row" style={{ gap: 'var(--space-xs)' }}>
              <Button size="sm" disabled={busy} onClick={() => onConfirm(message.proposal!)}>
                {proposalConfirmLabel(message.proposal)}
              </Button>
              <Button size="sm" emphasis="ghost" disabled={busy} onClick={() => onDismiss(message.proposal!)}>
                Don&apos;t
              </Button>
            </div>
          </div>
        )}
        {message.artifact && <ArtifactCard artifact={message.artifact} />}
      </div>
    </div>
  );
}

function ArtifactCard({ artifact }: { artifact: ChatArtifact }) {
  const body = [artifact.title, artifact.period, '', artifact.markdown].filter(Boolean).join('\n');
  return (
    <div className="chat-artifact">
      <strong>{artifact.title}</strong>
      {artifact.period && <p className="section__meta">{artifact.period}</p>}
      <pre>{artifact.markdown}</pre>
      <div className="row" style={{ gap: 'var(--space-xs)' }}>
        <Button
          size="sm"
          emphasis="ghost"
          onClick={() => void navigator.clipboard.writeText(body)}
        >
          Copy
        </Button>
        <Button
          size="sm"
          emphasis="ghost"
          onClick={() => {
            const blob = new Blob([body], { type: 'text/plain;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${artifact.title.replace(/[^\w]+/g, '-').replace(/^-|-$/g, '') || 'report'}.txt`;
            link.click();
            URL.revokeObjectURL(url);
          }}
        >
          Download text
        </Button>
      </div>
    </div>
  );
}

